import { useCallback, useEffect, useRef } from 'react';

interface SwipeState {
  startX: number;
  startY: number;
  startTime: number;
  isSwiping: boolean;
}

interface UseSwipeNavigationOptions {
  enabled?: boolean;
  threshold?: number;
  velocityThreshold?: number;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

export function useSwipeNavigation({
  enabled = true,
  threshold = 60,
  velocityThreshold = 0.3,
  onSwipeLeft,
  onSwipeRight,
}: UseSwipeNavigationOptions) {
  const swipeState = useRef<SwipeState | null>(null);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled) return;
      const touch = e.touches[0];
      swipeState.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        isSwiping: false,
      };
    },
    [enabled]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!swipeState.current || !enabled) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - swipeState.current.startX;
      const deltaY = touch.clientY - swipeState.current.startY;

      // 判斷是否為水平滑動 (水平位移 > 垂直位移 * 1.5)
      if (Math.abs(deltaX) > Math.abs(deltaY) * 1.5 && Math.abs(deltaX) > 15) {
        swipeState.current.isSwiping = true;
      }
    },
    [enabled]
  );

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!swipeState.current || !enabled) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - swipeState.current.startX;
      const deltaTime = Date.now() - swipeState.current.startTime;
      const velocity = Math.abs(deltaX) / deltaTime;

      const shouldTrigger =
        (Math.abs(deltaX) > threshold || velocity > velocityThreshold) &&
        swipeState.current.isSwiping;

      if (shouldTrigger) {
        if (deltaX < 0) {
          onSwipeLeft?.();
        } else {
          onSwipeRight?.();
        }
      }

      swipeState.current = null;
    },
    [enabled, threshold, velocityThreshold, onSwipeLeft, onSwipeRight]
  );

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);
}
